/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IonPage,
  IonContent,
  IonLoading,
  IonAlert,
} from "@ionic/react";
import axios from "axios";
import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { useAuthInit } from "../Auth";
import {
  requestID,
  baseUrl,
  setUserSession,
  setBusinessData,
  config,
  APP_VERSION,
  APP_COPYRIGHT,
} from "../common/utils";
import { useBusinessContext } from "../context/business-context";
import { 
  Box, 
  Button, 
  Container, 
  InputAdornment, 
  Paper, 
  TextField, 
  Typography, 
  Grid,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Rating,
  Avatar
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { 
  PersonOutline, 
  LockOutlined, 
  HowToReg, 
  HelpOutline, 
  Login,
  RocketLaunch,
  Store,
  Groups,
  Event,
  AccountBalance,
  TrendingUp,
  Apps,
  Handyman,
  ArrowBack,
  Check,
  Star,
  CalendarMonth,
  Payments,
  Security,
  Menu as MenuIcon,
  Close as CloseIcon,
  ContentCopy,
  Devices,
  ErrorOutline,
  LockPerson,
  Refresh,
  School,
  Hotel,
  Phone,
  Email,
  WhatsApp,
  ArrowForward,
  FormatQuote,
  VerifiedUser,
  ArrowCircleRight,
  ArrowCircleLeft,
  PlayArrow,
  Pause,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { keyframes } from "@emotion/react";
import { generateDeviceId } from "../common/deviceFingerprint";
import { deviceApi } from "../common/deviceApi";

const gridAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Device Check Dialog Component
const DeviceCheckDialog = ({ 
  open, 
  deviceCode, 
  deviceHash, 
  loading, 
  onRetry, 
  onLogout 
}: { 
  open: boolean; 
  deviceCode: string; 
  deviceHash: string; 
  loading: boolean; 
  onRetry: () => void; 
  onLogout: () => void; 
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const copyToClipboard = (text: string): void => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    });
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'error.main'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)', 
          color: 'white', 
          py: 2, 
          textAlign: 'center' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <ErrorOutline />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              🔒 DEVICE VERIFICATION REQUIRED
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <ErrorOutline 
              sx={{ 
                fontSize: 60, 
                color: 'error.main',
                mb: 2,
                filter: 'drop-shadow(0px 4px 8px rgba(244, 67, 54, 0.3))'
              }} 
            />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'error.dark', mb: 1 }}>
              Access Blocked - Device Not Registered
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              This device is not authorized to access the system
            </Typography>
          </Box>

          <Alert 
            severity="error"
            icon={<LockPerson fontSize="large" />}
            sx={{ 
              mb: 3,
              textAlign: 'left',
              fontSize: '1.1rem',
              py: 2
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚠️ SECURITY RESTRICTION
            </Typography>
            Your device is not registered. For security reasons, administrative accounts require device registration. 
            Your access has been blocked until this device is registered by an administrator.
          </Alert>

          <Box 
            sx={{ 
              mb: 3, 
              p: 3, 
              bgcolor: 'warning.light', 
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'warning.main'
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                color: 'warning.dark',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <Devices /> DEVICE REGISTRATION CODE
            </Typography>
            
            <Paper
              elevation={2}
              sx={{
                p: 2,
                mt: 2,
                mb: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                position: 'relative'
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  fontSize: '1rem',
                  lineHeight: 1.5,
                  letterSpacing: '0.5px'
                }}
              >
                {deviceCode}
              </Typography>
              
              <IconButton
                onClick={() => copyToClipboard(deviceCode)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
                size="small"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Paper>
            
            <Alert severity="info" sx={{ mb: 0 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Instructions:
              </Typography>
              1. Copy the device code above<br />
              2. Contact your system administrator<br />
              3. Provide them with this code<br />
              4. Wait for them to register your device<br />
              5. Click "Check Again" to continue
            </Alert>
          </Box>

          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Note:</strong> This security measure protects sensitive administrative functions. 
              Device registration ensures that only authorized devices can access the system.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onRetry}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={24} /> : <Refresh />}
            size="large"
            sx={{ mr: 2 }}
          >
            {loading ? 'Checking...' : 'Check Again'}
          </Button>
          
          <Button
            variant="outlined"
            color="secondary"
            onClick={onLogout}
            disabled={loading}
            startIcon={<ArrowBack />}
            size="large"
          >
            Back to Login
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Device code copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

// Testimonial Component
const TestimonialCard = ({ name, role, company, content, rating, avatarColor, isMobile }: {
  name: string;
  role: string;
  company: string;
  content: string;
  rating: number;
  avatarColor: string;
  isMobile: boolean;
}) => (
  <Card sx={{ 
    height: '100%', 
    display: 'flex', 
    flexDirection: 'column',
    borderRadius: 3,
    backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(10px)',
    border: '2px solid white',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
      backgroundColor: isMobile ? 'white' : 'rgba(255, 255, 255, 0.25)',
    }
  }}>
    <CardContent sx={{ flex: 1, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Avatar sx={{ bgcolor: avatarColor, mr: 1.5, width: 44, height: 44, border: '2px solid white' }}>
          {name.charAt(0)}
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: isMobile ? 'text.primary' : 'white', lineHeight: 1.2 }}>
            {name}
          </Typography>
          <Typography variant="caption" sx={{ color: isMobile ? 'text.secondary' : 'rgba(255, 255, 255, 0.8)', display: 'block' }}>
            {role} • {company}
          </Typography>
          <Rating value={rating} readOnly size="small" sx={{ '& .MuiRating-iconFilled': { color: '#ffc107' } }} />
        </Box>
      </Box>
      <FormatQuote sx={{ color: isMobile ? 'primary.main' : '#fff8e1', fontSize: 20, mb: 0.5 }} />
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: isMobile ? 'text.secondary' : 'white', lineHeight: 1.4 }}>
        "{content}"
      </Typography>
    </CardContent>
  </Card>
);

// Testimonial Carousel Component
const TestimonialCarousel = ({ testimonials, isMobile }: { 
  testimonials: any[]; 
  isMobile: boolean;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = isMobile ? 1 : 2;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + itemsPerPage) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length, itemsPerPage]);

  const currentTestimonials = testimonials.slice(currentIndex, currentIndex + itemsPerPage);
  // Handle wrap around if necessary
  if (currentTestimonials.length < itemsPerPage) {
    currentTestimonials.push(...testimonials.slice(0, itemsPerPage - currentTestimonials.length));
  }

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: '180px' }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.5 }}
        >
          <Grid container spacing={2}>
            {currentTestimonials.map((t, i) => (
              <Grid item xs={12} sm={6} key={`${currentIndex}-${i}`}>
                <TestimonialCard {...t} isMobile={isMobile} />
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </AnimatePresence>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5, gap: 0.5 }}>
        {Array.from({ length: Math.ceil(testimonials.length / itemsPerPage) }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: Math.floor(currentIndex / itemsPerPage) === i ? 'white' : 'rgba(255,255,255,0.3)',
              transition: 'all 0.3s ease'
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

// Animated Carousel Component
const PackageCarousel = ({ packages, onSelectPackage, isMobile }: { 
  packages: any[]; 
  onSelectPackage: (pkg: any) => void;
  isMobile: boolean;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % packages.length);
      }, 3500);
      return () => clearInterval(interval);
    }
  }, [packages.length, isPaused]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % packages.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + packages.length) % packages.length);
  };

  return (
    <Box 
      sx={{ position: 'relative', overflow: 'hidden' }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <Box sx={{ 
        display: 'flex', 
        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: `translateX(-${currentIndex * 100}%)`,
      }}>
        {packages.map((pkg, index) => (
          <Box key={pkg.id} sx={{ minWidth: '100%', p: 1 }}>
            <Card 
              sx={{ 
                background: `linear-gradient(135deg, ${pkg.color}dd 0%, ${pkg.color}aa 100%)`,
                border: `2px solid white`,
                borderRadius: 4,
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: `0 12px 40px rgba(0,0,0,0.3)`,
                  transform: 'translateY(-2px)'
                }
              }}
              onClick={() => onSelectPackage(pkg)}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      border: `2px solid white`,
                      mr: 2
                    }}
                  >
                    {React.cloneElement(pkg.icon, { sx: { fontSize: 26, color: 'white' } })}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="white">
                      {pkg.title}
                    </Typography>
                    <Typography variant="caption" color="white" sx={{ opacity: 0.9, display: 'block' }}>
                      {pkg.description}
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={1} sx={{ mb: 1.5 }}>
                  {pkg.features.slice(0, 3).map((feature: string, idx: number) => (
                    <Grid item xs={4} key={idx}>
                      <Chip
                        label={feature}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.15)',
                          color: 'white',
                          border: `1px solid white`,
                          fontWeight: 'bold',
                          width: '100%',
                          fontSize: '0.65rem',
                          height: '24px'
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  p: 1.5,
                  borderRadius: 2,
                  mb: 1.5,
                  border: '1px solid white'
                }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="medium"
                    sx={{
                      background: `white`,
                      color: pkg.color,
                      borderRadius: 2,
                      py: 1,
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      '&:hover': {
                        background: '#f0f0f0',
                      }
                    }}
                    endIcon={<ArrowForward fontSize="small" />}
                  >
                    🚀 GET STARTED
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="white" sx={{ opacity: 0.9, fontWeight: 'bold' }}>
                    📞 081300081375
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<WhatsApp sx={{ fontSize: '14px' }} />}
                    onClick={() => window.open('https://wa.me/23481300081375', '_blank')}
                    sx={{ 
                      borderColor: 'white', 
                      color: 'white',
                      fontSize: '0.7rem',
                      height: '24px',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        borderColor: 'white'
                      }
                    }}
                  >
                    WhatsApp
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        mt: 1,
        gap: 1
      }}>
        <IconButton onClick={prevSlide} size="small" sx={{ color: 'white' }}>
          <ArrowCircleLeft fontSize="small" />
        </IconButton>
        
        <IconButton onClick={() => setIsPaused(!isPaused)} size="small" sx={{ color: 'white' }}>
          {isPaused ? <PlayArrow fontSize="small" /> : <Pause fontSize="small" />}
        </IconButton>
        
        <IconButton onClick={nextSlide} size="small" sx={{ color: 'white' }}>
          <ArrowCircleRight fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const LoginPage: React.FC = () => {
  const [isInternet, setIsInternet] = useState({ openAlert: false, message: "" });
  const [status, setStatus] = useState({ loading: false, error: false });
  const authInitState = useAuthInit();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [showPackageDetails, setShowPackageDetails] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const { setSelectedBusiness } = useBusinessContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Device Check States
  const [deviceCheckDialogOpen, setDeviceCheckDialogOpen] = useState(false);
  const [deviceCheckLoading, setDeviceCheckLoading] = useState(false);
  const [deviceCode, setDeviceCode] = useState<string>('');
  const [deviceHash, setDeviceHash] = useState<string>('');
  const [userData, setUserData] = useState<any>(null);
  const [userDataHash, setUserDataHash] = useState<any>(null);

  const [deviceCheckResult, setDeviceCheckResult] = useState<any>(null);

  // Device Check Functions
  const performDeviceCheck = async (userId: string): Promise<boolean> => {
    setDeviceCheckLoading(true);
    try {
      const deviceId = generateDeviceId(userId);
      setDeviceCode(deviceId.base64);
      setDeviceHash(await deviceId.hash);
        
      const checkResult = await deviceApi.checkDeviceRegistration(userId, deviceId.hash, deviceId.base64);
      setDeviceCheckResult(checkResult);
      
      if (checkResult.status === true && checkResult.data?.device_registered === true) {
        return false;
      }
      return true;
      
    } catch (err: any) {
      setDeviceCheckResult({ 
        status: false, 
        data: { device_registered: false } 
      });
      return false;
    } finally {
      setDeviceCheckLoading(false);
    }
  };

  const check_device = async (userId: string) => {
    const deviceId = generateDeviceId(userId);
    try {
      const response = await axios.post(
        baseUrl() + "users/check_device",
        {
          user_id: userId,
          device_id: deviceId.base64,
          device_hash: await deviceId.hash, 
          requestID: requestID(),
        },
        config
      );
      return response.data;
    } catch (err: any) {
      return false;
    }
  };

  const handleRetryDeviceCheck = async () => {
    if (!userDataHash) return;
    const isRegistered = await performDeviceCheck(userDataHash.user_id);
    if (isRegistered) {
      setDeviceCheckDialogOpen(false);
      setUserSession(userData.access_token, userData);
      setBusinessData(userData.businessData);
      setSelectedBusiness(userData.businessData);
      authInitState.isLoggedIn = true;
      window.location.replace("/login");
    }
  };

  const handleLogoutFromDeviceCheck = () => {
    setDeviceCheckDialogOpen(false);
    setUserData(null);
    setDeviceCheckResult(null);
    setStatus({ loading: false, error: false });
  };
  
  // Software packages data - REMOVED ALL PRICING
  const softwarePackages = [
    {
      id: 1,
      title: "Cooperative Society",
      description: "Group savings, loans & financial management",
      icon: <Groups />,
      color: "#2196f3",
      category: "community",
      features: [
        "Member management portal", 
        "Savings/Contributions tracking", 
        "Loan application workflow", 
        "Automated interest & dividend calculation", 
        "Multi-branch synchronization", 
        "Mobile app for member self-service", 
        "SMS/Email notification system", 
        "Detailed financial auditing & reports", 
        "Multi-level administrative access", 
        "Contribution history records", 
        "Fixed deposit management", 
        "Investment tracking", 
        "Automated penal interest"
      ]
    },
    {
      id: 2,
      title: "Point of Sale System",
      description: "Sales, inventory & customer management",
      icon: <Store />,
      color: "#4caf50",
      category: "business",
      features: [
        "Real-time inventory management", 
        "Sales tracking & analytics", 
        "Barcode & QR scanning support", 
        "Professional receipt/invoice printing", 
        "Supplier & vendor management", 
        "Low stock & expiry alerts", 
        "Multiple payment method integration", 
        "Customer loyalty & discount programs", 
        "Comprehensive daily sales auditing", 
        "Online/Offline mode support", 
        "Credit sales tracking", 
        "Expense management", 
        "Profit & Loss dashboard"
      ]
    },
    {
      id: 3,
      title: "HR Management",
      description: "Payroll, attendance & employee records",
      icon: <AccountBalance />,
      color: "#ff9800",
      category: "business",
      features: [
        "Employee records database", 
        "Payroll processing", 
        "Attendance & leave tracking", 
        "Performance reviews", 
        "Recruitment management", 
        "Document storage", 
        "Employee self-service portal", 
        "Tax & pension compliance tools", 
        "Organizational charts", 
        "Biometric clock-in integration", 
        "Automatic tax calculations", 
        "Training & development tracker", 
        "Disciplinary history logs"
      ]
    },
    {
      id: 4,
      title: "Club Management",
      description: "Membership, events & payment processing",
      icon: <Event />,
      color: "#9c27b0",
      category: "community",
      features: [
        "Robust member database", 
        "Subscription & dues tracking", 
        "Event planning & RSVP system", 
        "Online payment collection", 
        "Configurable membership tiers", 
        "Internal communication tools", 
        "Digital ID card generation", 
        "Facility & amenity booking", 
        "Meeting attendance logs", 
        "Automatic renewal reminders", 
        "Guest pass management", 
        "Member feedback portal"
      ]
    },
    {
      id: 5,
      title: "Enterprise Accounting",
      description: "Financial management & reporting",
      icon: <TrendingUp />,
      color: "#f44336",
      category: "business",
      features: [
        "Full general ledger control", 
        "Accounts payable/receivable", 
        "Automated bank reconciliation", 
        "Tax compliance (VAT/WHT/CIT)", 
        "Expense tracking & categorization", 
        "Instant P&L & Balance sheets", 
        "Budgeting & cash flow forecasting", 
        "Multi-currency transaction support", 
        "Secure audit trail logs", 
        "Role-based user permissions", 
        "Asset depreciation tracking", 
        "Inventory value auditing", 
        "Custom financial report builder"
      ]
    },
    {
      id: 6,
      title: "Artisans Services",
      description: "Service booking & management platform",
      icon: <Handyman />,
      color: "#00bcd4",
      category: "community",
      features: [
        "Unified service booking system", 
        "Job scheduling & assignments", 
        "Customer review & rating system", 
        "Integrated payment gateway", 
        "Artisan mobile application", 
        "GPS-based geolocation search", 
        "Instant SMS/Push job alerts", 
        "Business performance analytics", 
        "Spare parts inventory control", 
        "Real-time job tracking", 
        "Automated service invoicing", 
        "Artisan payout management"
      ]
    },
    {
      id: 7,
      title: "CBT Management System",
      description: "Computer Based Testing & Exam Platform",
      icon: <School />,
      color: "#673ab7",
      category: "education",
      features: [
        "Scalable online exam creation", 
        "Secure browser & lockdown tools", 
        "Question/Option randomization", 
        "Instant automated grading", 
        "Question bank with categorization", 
        "In-depth result analytics", 
        "Professional certificate generation", 
        "Student self-service portal", 
        "Timed session controls", 
        "Bulk candidate registration portal", 
        "Anti-cheat proctoring tools", 
        "Multimedia question support", 
        "Parent/Teacher report portal"
      ]
    },
    {
      id: 8,
      title: "Hotel Management System",
      description: "Complete hotel & hospitality solution",
      icon: <Hotel />,
      color: "#009688",
      category: "business",
      features: [
        "Real-time room reservation system", 
        "Check-in/Check-out automation", 
        "Guest profile & history management", 
        "Housekeeping scheduling & logs", 
        "Integrated Restaurant/Bar POS", 
        "Hotel supply inventory tracking", 
        "Consolidated billing & invoicing", 
        "Occupancy & revenue analytics", 
        "Online booking channel manager", 
        "Event & Banquet hall booking", 
        "Staff task manager", 
        "Guest loyalty rewards"
      ]
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      name: "Adebayo Johnson",
      role: "CEO",
      company: "Greenfield Hotels",
      content: "The Hotel Management System transformed our operations. Revenue increased by 40%!",
      rating: 5,
      avatarColor: "#ff6b6b"
    },
    {
      name: "Chinwe Okonkwo",
      role: "Principal",
      company: "Excel Academy",
      content: "Our CBT system handles 5000+ students seamlessly. Saves us hundreds of hours monthly.",
      rating: 5,
      avatarColor: "#4ecdc4"
    },
    {
      name: "Musa Ibrahim",
      role: "Treasurer",
      company: "Unity Cooperative",
      content: "Best cooperative software! Members love the mobile app for tracking contributions.",
      rating: 5,
      avatarColor: "#45b7d1"
    },
    {
      name: "Funke Adeleke",
      role: "Manager",
      company: "SuperMart NG",
      content: "POS system is reliable. Inventory management is easy. Great support team!",
      rating: 5,
      avatarColor: "#96ceb4"
    }
  ];

  const filteredPackages = softwarePackages.filter(pkg => {
    if (activeTab === 0) return true;
    if (activeTab === 1) return pkg.category === "business";
    if (activeTab === 2) return pkg.category === "community";
    if (activeTab === 3) return pkg.category === "education";
    return true;
  });

  const handlePackageSelect = (pkg: any) => {
    setSelectedPackage(pkg);
    setShowPackageDetails(true);
  };

  const handleBackToList = () => {
    if (isMobile) {
      setSelectedPackage(null);
    } else {
      setShowPackageDetails(false);
      setSelectedPackage(null);
    }
  };

  const handleGetStarted = (packageName: string) => {
    history.push("/staff-register");
  };

  const schema = yup.object({
    password: yup.string().required("Password field is required."),
    email: yup.string().required("Email field is required."),
  }).required();

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  const handleLogin = async (data: any) => {
    setStatus({ loading: true, error: false });

    try {
      const response = await axios.post(
        baseUrl() + "users/loginfuncStaff",
        {
          username: data.email,
          password: data.password,
          requestID: requestID(),
        },
        config
      );

      if (response.data.status === true) {
        const userData = response.data.data;
        setUserDataHash(userData.loginData);
        const loggedin_device_id_status = userData.loginData.loggedin_device_id_status;
        const check_device_response = await check_device(userData.loginData.user_id);

        if(loggedin_device_id_status == 1 && check_device_response.data === 'mismatch' ){
          alert('Device ID mismatch');
          setStatus({ loading: false, error: false });
          return;
        }
        
        const requiresDeviceCheck = userData?.loginData.device_check_enabled ?? 0;
        const user_id = userData.loginData.user_id;

        if (requiresDeviceCheck) {
          const isDeviceRegistered = await performDeviceCheck(user_id);
          if (!isDeviceRegistered) {
            setDeviceCheckDialogOpen(true);
            setStatus({ loading: false, error: false });
            return;
          }
        }

        setUserData(userData);
        setUserSession(response.data.access_token, userData);
        setBusinessData(userData.businessData);
        setSelectedBusiness(userData.businessData);
        authInitState.isLoggedIn = true;

        window.location.replace("/login");
        return;
      }

      setStatus({ loading: false, error: true });
      setIsInternet({
        openAlert: true,
        message: response.data.message || "Login failed. Please try again.",
      });

    } catch (error: any) {
      setStatus({ loading: false, error: true });
      setIsInternet({
        openAlert: true,
        message: error.response?.data?.message || "An error occurred.",
      });
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Package Details Component - REMOVED PRICING SECTIONS
  const PackageDetails = () => {
    if (!selectedPackage) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Paper sx={{ 
          background: isMobile ? 'rgba(26, 41, 128, 0.95)' : 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(15px)',
          borderRadius: 4,
          overflow: 'hidden',
          border: `2px solid white`,
          color: 'white',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          mx: isMobile ? 0 : 2,
          boxShadow: '0 15px 50px rgba(0,0,0,0.3)'
        }}>
          <Box sx={{ 
            p: 2, 
            background: `linear-gradient(135deg, ${selectedPackage.color} 0%, ${selectedPackage.color}bb 100%)`,
            borderBottom: `2px solid white`,
            flexShrink: 0
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <IconButton 
                onClick={handleBackToList}
                sx={{ 
                  color: 'white',
                  mr: 1.5,
                  border: `2px solid white`,
                  p: 0.5,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  border: `2px solid white`,
                  mr: 2
                }}
              >
                {React.cloneElement(selectedPackage.icon, { sx: { fontSize: 24, color: 'white' } })}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: 'white', lineHeight: 1.2 }}>
                  {selectedPackage.title}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.95, color: 'white', display: 'block' }}>
                  {selectedPackage.description}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ 
            p: 2, 
            flex: 1, 
            overflow: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.05)' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '3px' }
          }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}><VerifiedUser sx={{ mr: 1, fontSize: 18 }} /> Features Included</Typography>
                <Box sx={{ p: 1.5, background: 'rgba(255,255,255,0.05)', borderRadius: 2, border: '1px solid white' }}>
                  <List dense sx={{ py: 0 }}>
                    {selectedPackage.features.map((feature: string, index: number) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 26 }}><Check sx={{ fontSize: 16, color: '#4caf50' }} /></ListItemIcon>
                        <ListItemText primary={feature} primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 500 } }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Button 
                variant="contained" 
                fullWidth 
                size="medium" 
                onClick={() => handleGetStarted(selectedPackage.title)} 
                sx={{ 
                  background: 'white', 
                  color: selectedPackage.color, 
                  borderRadius: 2, 
                  py: 1.5, 
                  fontWeight: 'bold', 
                  fontSize: '1rem', 
                  border: '2px solid white', 
                  '&:hover': { 
                    background: '#f8f9fa', 
                    transform: 'translateY(-2px)' 
                  } 
                }}
              >
                🚀 Get Started Now
              </Button>
            </Box>
          </Box>
        </Paper>
      </motion.div>
    );
  };

  // Package List Component with Carousel - UPDATED
  const PackageList = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ textAlign: 'center', mb: 1.5, flexShrink: 0, px: 1 }}>
        <Typography variant="h4" fontWeight="900" sx={{ color: 'white', textShadow: '0 3px 6px rgba(0,0,0,0.3)', fontSize: isMobile ? '1.5rem' : '2.2rem', mb: 0.5 }}>
          Gea-Cloud SaaS Platform
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.95, color: 'white', fontSize: isMobile ? '0.85rem' : '1rem' }}>
          Empowering 500+ Businesses Across Nigeria 🇳🇬
        </Typography>
      </Box>

      <Paper sx={{ 
        background: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(10px)',
        color: 'white',
        p: 1.5,
        mb: 2,
        borderRadius: 3,
        textAlign: 'center',
        border: '2px solid white',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
      }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>📞 CONTACT: 081300081375</Typography>
        <Grid container spacing={1} justifyContent="center">
          <Grid item><Chip label="Call" size="small" onClick={() => window.open('tel:081300081375')} sx={{ fontWeight: 'bold', border: '1px solid white', color: 'white', cursor: 'pointer' }} /></Grid>
          <Grid item><Chip label="WhatsApp" size="small" onClick={() => window.open('https://wa.me/23481300081375', '_blank')} sx={{ fontWeight: 'bold', bgcolor: '#25D366', color: 'white', border: '1px solid white', cursor: 'pointer' }} /></Grid>
          <Grid item><Chip label="Email" size="small" onClick={() => window.open('mailto:info@appstack.ng')} sx={{ fontWeight: 'bold', border: '1px solid white', color: 'white', cursor: 'pointer' }} /></Grid>
        </Grid>
      </Paper>

      <Box sx={{ mb: 1, flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: '40px',
            '& .MuiTab-root': {
              color: 'rgba(255,255,255,0.7)',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              minHeight: '40px',
              '&.Mui-selected': { color: 'white' }
            },
            '& .MuiTabs-indicator': { backgroundColor: 'white' }
          }}
        >
          <Tab label="All" />
          <Tab label="Business" />
          <Tab label="Community" />
          <Tab label="Education" />
        </Tabs>
      </Box>

      <Box sx={{ flexShrink: 0, mb: 1 }}>
        <PackageCarousel 
          packages={filteredPackages} 
          onSelectPackage={handlePackageSelect}
          isMobile={isMobile}
        />
      </Box>

      <Box sx={{ px: 1, mb: 1, flex: 1, overflow: 'auto' }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'white', textAlign: 'center', mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
          <VerifiedUser sx={{ verticalAlign: 'middle', mr: 1, fontSize: 18 }} /> Client Success Stories
        </Typography>
        <TestimonialCarousel testimonials={testimonials} isMobile={isMobile} />
      </Box>

      <Box sx={{ textAlign: 'center', py: 1, flexShrink: 0 }}>
        <Button variant="contained" size="large" startIcon={<RocketLaunch />} onClick={() => handleGetStarted("All")} sx={{ backgroundColor: 'white', color: '#1a2980', fontWeight: '900', px: 4, borderRadius: 3, border: '2px solid white', '&:hover': { backgroundColor: '#f0f0f0', transform: 'scale(1.05)' } }}>
          GET STARTED 🚀
        </Button>
      </Box>
    </motion.div>
  );

  return (
    <IonPage>
      <IonContent fullscreen style={{ '--overflow': 'hidden' } as any}>
        <DeviceCheckDialog
          open={deviceCheckDialogOpen}
          deviceCode={deviceCode}
          deviceHash={deviceHash}
          loading={deviceCheckLoading}
          onRetry={handleRetryDeviceCheck}
          onLogout={handleLogoutFromDeviceCheck}
        />

        {isMobile && !showPackageDetails && (
          <IconButton
            sx={{
              position: 'fixed',
              top: 16,
              left: 16,
              zIndex: 1200,
              backgroundColor: 'white',
              boxShadow: 2,
              '&:hover': { backgroundColor: 'white' }
            }}
            onClick={() => { setShowPackageDetails(true); setSelectedPackage(softwarePackages[0]); }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Grid container sx={{ 
          height: '100vh', 
          overflow: 'hidden',
          margin: 0,
          flexDirection: isMobile ? 'column-reverse' : 'row'
        }}>
          {!isMobile ? (
            <Grid item xs={12} md={6} sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 3,
              color: 'white',
              background: 'linear-gradient(135deg, #1a2980 0%, #26d0ce 100%)',
              position: 'relative',
              overflow: 'hidden',
              height: '100vh',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: `linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.03) 75%)`,
                backgroundSize: '30px 30px',
                animation: `${gridAnimation} 15s ease infinite`,
              }
            }}>
              <Box sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <AnimatePresence mode="wait">
                  {showPackageDetails ? <PackageDetails /> : <PackageList />}
                </AnimatePresence>
              </Box>
            </Grid>
          ) : null}

          <Grid item xs={12} md={isMobile ? 12 : 6} sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f4f7f6',
            p: isMobile ? 1.5 : 3,
            height: '100vh',
            overflow: 'auto',
            order: isMobile ? 1 : 2
          }}>
            <Container maxWidth="xs" sx={{ p: isMobile ? 0 : 2 }}>
              <Paper elevation={isMobile ? 0 : 10} sx={{ 
                p: isMobile ? 2.5 : 4, 
                borderRadius: 4,
                backgroundColor: 'white',
                boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0,0,0,0.1)'
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Avatar sx={{ width: isMobile ? 64 : 80, height: isMobile ? 64 : 80, mb: 2, bgcolor: 'primary.main', boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }}>
                    <Login sx={{ fontSize: isMobile ? 32 : 40 }} />
                  </Avatar>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="900" color="primary" gutterBottom>Welcome Back</Typography>
                  <Typography variant="body2" color="textSecondary" mb={3}>Login to manage your business</Typography>

                  <Box component="form" onSubmit={handleSubmit(handleLogin)} noValidate sx={{ width: '100%' }}>
                    <TextField fullWidth variant="outlined" label="Email / Username" size="small" error={!!errors.email} helperText={errors.email?.message} {...register("email")} InputProps={{ startAdornment: (<InputAdornment position="start"><PersonOutline fontSize="small" /></InputAdornment>) }} sx={{ mb: 1.5 }} />
                    <TextField fullWidth variant="outlined" type="password" label="Password" size="small" error={!!errors.password} helperText={errors.password?.message} {...register("password")} InputProps={{ startAdornment: (<InputAdornment position="start"><LockOutlined fontSize="small" /></InputAdornment>) }} sx={{ mb: 2.5 }} />
                    
                    <Button type="submit" fullWidth variant="contained" size="large" disabled={status.loading} sx={{ py: 1.5, borderRadius: 3, fontWeight: '900', background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', mb: 2, '&:hover': { transform: 'translateY(-2px)' } }}>
                      {status.loading ? <CircularProgress size={24} color="inherit" /> : 'SECURE LOGIN'}
                    </Button>

                    <Grid container justifyContent="space-between">
                      <Button component={Link} to="/staff-register" size="small" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Create Account</Button>
                      <Button component={Link} to="/forgot_password" size="small" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Forgot Password?</Button>
                    </Grid>
                    
                    {isMobile && !showPackageDetails && (
                      <Button fullWidth variant="outlined" onClick={() => { setShowPackageDetails(true); setSelectedPackage(softwarePackages[0]); }} sx={{ mt: 2, borderRadius: 3, fontWeight: 'bold', border: '2px solid' }}>VIEW SOLUTIONS</Button>
                    )}
                  </Box>
                </Box>
              </Paper>
              <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 3, color: 'text.secondary', opacity: 0.8 }}>
                Version {APP_VERSION} • © {new Date().getFullYear()} {APP_COPYRIGHT}
              </Typography>
            </Container>
          </Grid>
          
          {isMobile && showPackageDetails && (
            <Grid item xs={12} sx={{
              order: 3,
              background: 'linear-gradient(135deg, #1a2980 0%, #26d0ce 100%)',
              height: '85vh',
              overflow: 'auto',
              borderTop: '3px solid white'
            }}>
              <Box sx={{ p: 2, height: '100%' }}>
                <AnimatePresence mode="wait">
                  {selectedPackage ? <PackageDetails /> : <PackageList />}
                </AnimatePresence>
              </Box>
            </Grid>
          )}
        </Grid>
        <IonLoading isOpen={status.loading} message="Authenticating..." />
        <IonAlert isOpen={isInternet.openAlert} header="Login Alert" message={isInternet.message} buttons={['OK']} onDidDismiss={() => setIsInternet({ openAlert: false, message: "" })} />
      </IonContent>
    </IonPage>
  );
};

export default LoginPage;